/** Stages shown to the user while a check runs. */
export type TestStage = 'connecting' | 'latency' | 'download' | 'upload' | 'analyzing';

/** Idle latency, download and download-loaded latency, from the Cloudflare speed test engine. */
export interface EngineRaw {
  downloadMbps: number | null;
  idleLatencyMs: number | null;
  idleJitterMs: number | null;
  /** Latency while the download runs (the engine's "loaded latency"). */
  loadedLatencyMs: number | null;
  loadedJitterMs: number | null;
  idleLatencySamples: number;
  loadedLatencySamples: number;
  downloadBytes: number;
  durationMs: number;
  /** nextHopProtocol of the measurement requests, e.g. ["h2"]. */
  protocols: string[];
}

/** One completed upload request, times relative to the start of the upload phase. */
export interface UploadTimelineEntry {
  stream: number;
  startMs: number;
  endMs: number;
  bytes: number;
}

/** Upload throughput from Method A (docs/measurement-research.md §16, §19). */
export interface UploadRaw {
  mbps: number | null;
  attributedBytes: number;
  bytesSent: number;
  requests: number;
  maxChunkBytes: number;
  durationMs: number;
  protocols: string[];
  errors: string[];
  timeline: UploadTimelineEntry[];
}

export interface RawMeasurementResult {
  provider: string;
  /** Origin the measurements ran against. */
  endpoint: string;
  startedAt: string;
  durationMs: number;
  engine: EngineRaw;
  upload: UploadRaw;
}

export interface TestOptions {
  onStage?: (stage: TestStage) => void;
  signal?: AbortSignal;
}

export interface MeasurementProvider {
  readonly name: string;
  startTest(options?: TestOptions): Promise<RawMeasurementResult>;
}

export type MeasurementErrorCode =
  | 'cancelled'
  | 'timeout'
  | 'interrupted'
  | 'network'
  | 'server'
  | 'incomplete'
  | 'unknown';

/** The only error type that leaves the measurement layer. `details` is for debug mode, never for users. */
export class MeasurementError extends Error {
  constructor(
    readonly code: MeasurementErrorCode,
    message: string,
    readonly stage?: TestStage,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'MeasurementError';
  }
}

export function toMeasurementError(error: unknown, stage?: TestStage): MeasurementError {
  if (error instanceof MeasurementError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new MeasurementError('cancelled', 'The check was stopped.', stage);
  }
  return new MeasurementError('unknown', error instanceof Error ? error.message : String(error), stage);
}
